import React, { useState, useEffect, useCallback } from 'react';
import { useSprings, animated, to as interpolate } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import JobCard from './JobCard';
import SwipeActions from './SwipeActions';
import SwipeResult from './SwipeResult';
import './SwipeView.css';
import {
  getSwipeQueue,
  getSwipeStats,
  canUserSwipe,
  canUserApply,
  recordSwipe,
  dbSwipeQueueItemToApp,
} from '../supabaseClient';

/**
 * SwipeView - Main swipe interface for Daily Match feature
 *
 * Gestures:
 * - Swipe RIGHT: Apply with AI-optimized resume
 * - Swipe LEFT: Pass/Skip
 * - Swipe UP: Defer for later
 */

// Animation helpers
const to = (i) => ({
  x: 0,
  y: i * -4,
  scale: 1 - i * 0.05,
  rot: -5 + Math.random() * 10,
  delay: i * 100,
});

const from = () => ({ x: 0, rot: 0, scale: 1.5, y: -1000 });

// Interpolation for card transform
const trans = (r, s) =>
  `perspective(1500px) rotateX(10deg) rotateY(${r / 10}deg) rotateZ(${r}deg) scale(${s})`;

const SwipeView = ({
  userId,
  profile,
  effectiveTier,
  onGenerateResume,
  onShowPaywall,
}) => {
  // State
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [swipeResult, setSwipeResult] = useState(null);
  const [swipeLimits, setSwipeLimits] = useState({ canSwipe: true, remaining: -1 });
  const [applyLimits, setApplyLimits] = useState({ canGenerate: true, remaining: -1 });
  const [gone] = useState(() => new Set());

  // Load queue and check limits
  const loadQueue = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    // Clear gone set so re-loaded cards are visible
    gone.clear();

    try {
      // Load queue, stats, and limits in parallel
      const [queueResult, statsResult, swipeCheck, applyCheck] = await Promise.all([
        getSwipeQueue(userId),
        getSwipeStats(userId),
        canUserSwipe(userId),
        canUserApply(userId),
      ]);

      // If there's an error, show a meaningful message based on error type
      if (queueResult.error) {
        const errMsg = queueResult.error;
        if (errMsg.includes('does not exist') || errMsg.includes('relation')) {
          setError('Daily Match tables are being set up. Please try again in a moment.');
        } else if (errMsg.includes('Database not configured')) {
          setError('Database connection issue. Please try again later.');
        } else {
          // Still show empty state for unknown errors, but log them
          console.warn('Queue load error:', errMsg);
        }
        setQueue([]);
        setStats({ total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 });
        setSwipeLimits(swipeCheck || { canSwipe: true, remaining: 10 });
        setApplyLimits(applyCheck || { canGenerate: true, remaining: 3 });
        return;
      }

      // Convert to app format
      const appQueue = (queueResult.data || []).map(dbSwipeQueueItemToApp);
      setQueue(appQueue);
      setStats(statsResult.stats || { total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 });
      setSwipeLimits(swipeCheck || { canSwipe: true, remaining: 10 });
      setApplyLimits(applyCheck || { canGenerate: true, remaining: 3 });

    } catch (err) {
      console.error('Error loading swipe queue:', err);
      setError('Unable to load matches. Check your connection and try again.');
      setQueue([]);
      setStats({ total: 0, pending: 0, applied: 0, passed: 0, deferred: 0 });
      setSwipeLimits({ canSwipe: true, remaining: 10 });
      setApplyLimits({ canGenerate: true, remaining: 3 });
    } finally {
      setLoading(false);
    }
  }, [userId, gone]);

  // Generate queue if empty
  const generateQueue = useCallback(async () => {
    if (!userId || !profile) return;

    // Check if profile has required fields
    if (!profile.targetRole) {
      setError('Please set your target role in your profile first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/swipe-queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          profile: {
            targetRole: profile.targetRole,
            location: profile.location,
            skills: profile.skills,
            experiences: profile.experiences,
            salaryMin: profile.targetSalaryMin,
            salaryMax: profile.targetSalaryMax,
            swipePreferences: profile.swipePreferences,
          },
          limit: 10,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        // Check for database setup errors
        if (data.error?.includes('does not exist') || data.error?.includes('relation')) {
          setError('Daily Match tables are being set up. Please try again in a moment.');
        } else {
          setError(data.error || 'Failed to generate matches. Please try again.');
        }
        return;
      }

      // Log diagnostics from the pipeline
      if (data.diagnostics) {
        console.log('Queue generation diagnostics:', data.diagnostics);
      }

      // Reload queue after generation
      await loadQueue();

    } catch (err) {
      console.error('Error generating queue:', err);
      setError('Unable to connect. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, profile, loadQueue]);

  // Initial load
  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Handle swipe action
  const handleSwipe = useCallback(async (index, direction) => {
    const item = queue[index];
    if (!item) return;

    // Check limits
    if (!swipeLimits.canSwipe && swipeLimits.remaining === 0) {
      onShowPaywall?.('swipe');
      return;
    }

    // For right swipe (apply), check resume limit
    if (direction === 'right') {
      if (!applyLimits.canGenerate && applyLimits.remaining === 0) {
        onShowPaywall?.('resume');
        return;
      }
    }

    // Get job data
    const jobData = item.externalJobData || {
      company: item.company,
      role: item.role,
      title: item.title,
    };

    // Record the swipe
    const result = await recordSwipe(userId, item.id, direction, jobData);

    if (result.error) {
      console.error('Failed to record swipe:', result.error);
      return;
    }

    // Update local state
    gone.add(index);

    // For right swipe, generate resume
    if (direction === 'right') {
      setGenerating(true);
      setSwipeResult({
        type: 'applying',
        job: jobData,
        queueItem: item,
      });

      try {
        // Call the resume generation callback
        if (onGenerateResume) {
          const resume = await onGenerateResume(item, jobData);
          setSwipeResult({
            type: 'applied',
            job: jobData,
            queueItem: item,
            resume,
          });
        } else {
          setSwipeResult({
            type: 'applied',
            job: jobData,
            queueItem: item,
          });
        }
      } catch (err) {
        console.error('Resume generation failed:', err);
        setSwipeResult({
          type: 'applied',
          job: jobData,
          queueItem: item,
          error: 'Resume generation failed',
        });
      } finally {
        setGenerating(false);
      }
    } else if (direction === 'up') {
      // Brief "deferred" feedback
      setSwipeResult({
        type: 'deferred',
        job: jobData,
      });
      setTimeout(() => setSwipeResult(null), 1500);
    }

    // Update stats
    setStats(prev => ({
      ...prev,
      pending: Math.max(0, prev.pending - 1),
      applied: direction === 'right' ? prev.applied + 1 : prev.applied,
      passed: direction === 'left' ? prev.passed + 1 : prev.passed,
      deferred: direction === 'up' ? prev.deferred + 1 : prev.deferred,
    }));

    // Update limits
    setSwipeLimits(prev => ({
      ...prev,
      remaining: prev.remaining > 0 ? prev.remaining - 1 : prev.remaining,
    }));

  }, [queue, userId, swipeLimits, applyLimits, gone, onGenerateResume, onShowPaywall]);

  // Spring animations for cards
  const [props, api] = useSprings(queue.length, (i) => ({
    ...to(i),
    from: from(),
  }));

  // Drag gesture handler
  const bind = useDrag(({ args: [index], active, movement: [mx, my], direction: [xDir, yDir], velocity: [vx, vy] }) => {
    // Determine swipe direction based on movement
    const triggerX = vx > 0.2 || Math.abs(mx) > 100;
    const triggerY = vy > 0.2 && my < -50;

    // Only trigger if not active and threshold met
    if (!active && (triggerX || triggerY)) {
      let direction;
      if (triggerY && Math.abs(my) > Math.abs(mx)) {
        direction = 'up';
      } else if (mx > 0) {
        direction = 'right';
      } else {
        direction = 'left';
      }

      gone.add(index);
      handleSwipe(index, direction);
    }

    api.start((i) => {
      if (index !== i) return;
      const isGone = gone.has(index);

      // Calculate exit position
      let x = 0;
      let y = 0;
      if (isGone) {
        const dir = mx > 0 ? 1 : -1;
        if (my < -50) {
          y = -window.innerHeight; // Up
        } else {
          x = (200 + window.innerWidth) * dir; // Left/Right
        }
      } else if (active) {
        x = mx;
        y = my;
      }

      const rot = mx / 100 + (isGone ? xDir * 10 * vx : 0);
      const scale = active ? 1.1 : 1;

      return {
        x,
        y,
        rot,
        scale,
        delay: undefined,
        config: { friction: 50, tension: active ? 800 : isGone ? 200 : 500 },
      };
    });
  });

  // Handle button clicks (accessibility)
  const handleButtonSwipe = (direction) => {
    const currentIndex = queue.findIndex((_, i) => !gone.has(i));
    if (currentIndex === -1) return;

    // Animate out
    api.start((i) => {
      if (i !== currentIndex) return;
      gone.add(currentIndex);

      let x = 0;
      let y = 0;
      if (direction === 'up') {
        y = -window.innerHeight;
      } else if (direction === 'right') {
        x = window.innerWidth + 200;
      } else {
        x = -(window.innerWidth + 200);
      }

      return { x, y, rot: direction === 'up' ? 0 : direction === 'right' ? 30 : -30, scale: 1 };
    });

    handleSwipe(currentIndex, direction);
  };

  // Continue after result
  const handleContinue = () => {
    setSwipeResult(null);
  };

  // Current card index
  const currentIndex = queue.findIndex((_, i) => !gone.has(i));
  const currentItem = currentIndex >= 0 ? queue[currentIndex] : null;
  const remainingCount = queue.length - gone.size;

  // Loading state
  if (loading) {
    return (
      <div className="swipe-view swipe-view--loading">
        <div className="swipe-loading">
          <div className="swipe-loading__spinner" />
          <p>Finding your daily matches...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="swipe-view swipe-view--error">
        <div className="swipe-error">
          <div className="swipe-error__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <p>{error}</p>
          <button onClick={loadQueue} className="swipe-error__retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state - need to generate queue
  if (queue.length === 0) {
    const hasTargetRole = profile?.targetRole;

    return (
      <div className="swipe-view swipe-view--empty">
        <div className="swipe-empty">
          <div className="swipe-empty__icon-wrapper">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="6"/>
              <circle cx="12" cy="12" r="2"/>
            </svg>
          </div>
          <h3>Daily Match</h3>
          {hasTargetRole ? (
            <>
              <p>Get 10 curated jobs matched to your profile every day.</p>
              <p className="swipe-empty__subtitle">
                Swipe right to apply with an AI-optimized resume.
              </p>
              <button onClick={generateQueue} className="swipe-empty__generate">
                Find My Matches
              </button>
            </>
          ) : (
            <>
              <p>Set your target role in your profile to get started.</p>
              <p className="swipe-empty__subtitle">
                We'll find jobs that match your skills and experience.
              </p>
              <button className="swipe-empty__generate swipe-empty__generate--disabled" disabled>
                Complete Profile First
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // All done state
  if (remainingCount === 0 && !swipeResult) {
    return (
      <div className="swipe-view swipe-view--done">
        <div className="swipe-done">
          <div className="swipe-done__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h3>All caught up!</h3>
          <p>You've reviewed all {stats.total} jobs today.</p>
          <div className="swipe-done__stats">
            <div className="swipe-done__stat">
              <span className="swipe-done__stat-value">{stats.applied}</span>
              <span className="swipe-done__stat-label">Applied</span>
            </div>
            <div className="swipe-done__stat">
              <span className="swipe-done__stat-value">{stats.deferred}</span>
              <span className="swipe-done__stat-label">Saved</span>
            </div>
            <div className="swipe-done__stat">
              <span className="swipe-done__stat-value">{stats.passed}</span>
              <span className="swipe-done__stat-label">Passed</span>
            </div>
          </div>
          <p className="swipe-done__subtitle">Come back tomorrow for new matches!</p>
        </div>
      </div>
    );
  }

  // Show result overlay
  if (swipeResult) {
    return (
      <div className="swipe-view">
        <SwipeResult
          result={swipeResult}
          generating={generating}
          onContinue={handleContinue}
        />
      </div>
    );
  }

  // Main swipe interface
  return (
    <div className="swipe-view">
      {/* Header */}
      <div className="swipe-header">
        <div className="swipe-header__title">
          <span className="swipe-header__icon">🎯</span>
          Daily Match
        </div>
        <div className="swipe-header__progress">
          {remainingCount} of {queue.length} remaining
        </div>
      </div>

      {/* Card stack */}
      <div className="swipe-stack">
        {props.map(({ x, y, rot, scale }, i) => {
          const item = queue[i];
          if (gone.has(i)) return null;

          return (
            <animated.div
              key={item.id}
              className="swipe-card-wrapper"
              style={{
                transform: interpolate([x, y], (x, y) => `translate3d(${x}px,${y}px,0)`),
                zIndex: queue.length - i,
              }}
            >
              <animated.div
                {...bind(i)}
                style={{
                  transform: interpolate([rot, scale], trans),
                  touchAction: 'none',
                }}
              >
                <JobCard
                  item={item}
                  isActive={i === currentIndex}
                />
              </animated.div>
            </animated.div>
          );
        })}
      </div>

      {/* Action buttons */}
      <SwipeActions
        onSwipe={handleButtonSwipe}
        disabled={!currentItem}
        canApply={applyLimits.canGenerate || applyLimits.remaining > 0}
        swipesRemaining={swipeLimits.remaining}
        appliesRemaining={applyLimits.remaining}
        effectiveTier={effectiveTier}
      />

      {/* Tier indicator */}
      {effectiveTier === 'free' && (
        <div className="swipe-tier-notice">
          <span>
            {swipeLimits.remaining > 0
              ? `${swipeLimits.remaining} swipes left today`
              : 'Daily limit reached'}
          </span>
          {swipeLimits.remaining <= 3 && (
            <button onClick={() => onShowPaywall?.('swipe')} className="swipe-tier-notice__upgrade">
              Upgrade for unlimited
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SwipeView;
